import { Helmet } from 'react-helmet-async';

interface Alternate {
    hreflang: string;
    href: string;
}

interface SEOProps {
    title: string;
    description?: string;
    canonical?: string;
    ogImage?: string;
    ogType?: 'website' | 'article' | 'product';
    noIndex?: boolean;
    /** Single object or array of JSON-LD graphs (each rendered as a separate <script>) */
    jsonLd?: Record<string, any> | Array<Record<string, any>>;
    /** hreflang alternates for this page */
    alternates?: Alternate[];
    /** Sets <html lang="..."> */
    lang?: string;
}

const DEFAULT_DESCRIPTION =
    'CareOS é a plataforma de home care para famílias e empresas: plano de cuidados com IA, escala de cuidadores, sinais vitais e relatórios em tempo real.';
const DEFAULT_OG_IMAGE = 'https://careos.life/og-image.png';

export function SEO({
    title,
    description = DEFAULT_DESCRIPTION,
    canonical,
    ogImage = DEFAULT_OG_IMAGE,
    ogType = 'website',
    noIndex = false,
    jsonLd,
    alternates,
    lang,
}: SEOProps) {
    const siteTitle = 'CareOS';
    const fullTitle = title === siteTitle ? title : `${title} | ${siteTitle}`;
    const resolvedCanonical =
        canonical ??
        (typeof window !== 'undefined' && window.location.hostname !== 'localhost'
            ? `https://careos.life${window.location.pathname}`
            : undefined);
    const jsonLdArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

    return (
        <Helmet htmlAttributes={lang ? { lang } : undefined}>
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            {noIndex ? (
                <meta name="robots" content="noindex, nofollow" />
            ) : (
                <meta
                    name="robots"
                    content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
                />
            )}

            {resolvedCanonical && <link rel="canonical" href={resolvedCanonical} />}
            {alternates?.map((a) => (
                <link key={a.hreflang} rel="alternate" hrefLang={a.hreflang} href={a.href} />
            ))}

            <meta property="og:type" content={ogType} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            {resolvedCanonical && <meta property="og:url" content={resolvedCanonical} />}
            {ogImage && <meta property="og:image" content={ogImage} />}

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            {ogImage && <meta name="twitter:image" content={ogImage} />}

            {jsonLdArray.map((g, i) => (
                <script key={i} type="application/ld+json">
                    {JSON.stringify(g)}
                </script>
            ))}
        </Helmet>
    );
}
